/**
 * PvP Room layout — pixel-perfect match to design.
 * Scrollable: InfoBlock → PlayerActivity (live round feed) → Wheel → PlayersList
 * Fixed bottom: BettingPanel or TricksPanel (above BottomNav)
 * NOTE: No CSS pointer here — PixiJS engine renders its own pointer.
 *
 * Bottom padding on scroll content must clear the fixed betting bar (~pt-4 + preset row + gap + ~60px CTA + pb-3)
 * so the last pixels of the list are not hidden under the panel. Main layout already reserves the tab bar.
 */

import { InfoBlock } from "./InfoBlock"
import {
  PlayerActivity,
  PVP_PLAYER_ACTIVITY_SLOT_HEIGHT_PX,
  PVP_WHEEL_STACK_OVERLAP_PX,
} from "./PlayerActivity"
import { PvPRouletteWheel } from "./PvPRouletteWheel"
import { ResolvedWinnerSheet } from "./ResolvedWinnerSheet"
import { BettingPanel } from "./BettingPanel"
import { TricksPanel } from "./TricksPanel"
import { PlayersList } from "./PlayersList"
import { RoundFairnessHashes } from "./RoundFairnessHashes"
import { useDemoModeStore } from "@/shared/store"

interface PvPRoomLayoutProps {
  roomId: string
  engineOptions?: Parameters<typeof PvPRouletteWheel>[0]["engineOptions"]
  currentUserId?: string
}

export const PvPRoomLayout = ({
  roomId,
  engineOptions,
  currentUserId,
}: PvPRoomLayoutProps) => {
  const demoOverlayNowMs = useDemoModeStore((s) =>
    s.active ? s.replayNowMs : null
  )
  return (
    <>
      {/* Scrollable content — extra bottom inset for fixed BettingPanel / TricksPanel */}
      <div className="mx-auto max-w-2xl pb-[calc(148px+0.75rem)]">
        {/* Info block: history + top wins + balance */}
        <InfoBlock />

        {/* Activity: fixed-height reserve in flow; feed is absolutely layered so it never shifts the wheel. */}
        <div className="relative">
          <div
            className="relative z-[1] w-full shrink-0"
            style={{ height: PVP_PLAYER_ACTIVITY_SLOT_HEIGHT_PX }}
          >
            <div className="pointer-events-none absolute inset-x-0 bottom-0 top-0 overflow-hidden px-3">
              <PlayerActivity roomId={roomId} />
            </div>
          </div>
          <div
            className="relative z-10 px-3"
            style={{ marginTop: -PVP_WHEEL_STACK_OVERLAP_PX }}
          >
            <PvPRouletteWheel
              roomId={roomId}
              engineOptions={engineOptions}
              currentUserId={currentUserId}
              overlayNowMs={demoOverlayNowMs}
            />
          </div>
        </div>

        {/* Players list with Solo | Squad split */}
        <div className="px-3">
          <PlayersList roomId={roomId} currentUserId={currentUserId} />
          <RoundFairnessHashes roomId={roomId} />
        </div>
      </div>

      {/* Bottom offset matches BottomNav (74+safe); -9px = hairline + pb-3 vs pb-1 (+8px) so CTA/viewport Y unchanged; glow fits inside panel. */}
      <div className="fixed inset-x-0 bottom-[calc(74px+var(--app-safe-bottom)-9px)] z-40 overflow-visible rounded-t-2xl bg-primary-main drop-shadow-[0_-4px_10px_rgba(0,0,0,0.25)]">
        <div className="mx-auto max-w-[390px] overflow-visible">
          <BettingPanel roomId={roomId} currentUserId={currentUserId} />
          <TricksPanel roomId={roomId} currentUserId={currentUserId} />
        </div>
      </div>

      <ResolvedWinnerSheet roomId={roomId} currentUserId={currentUserId} />
    </>
  )
}
